import React, { ChangeEvent, FC, SyntheticEvent, useContext, useState } from 'react';
import styled from 'styled-components';
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Select,
  SelectChangeEvent,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  DateRange,
  ExpandLess,
  HourglassBottom,
  InfoOutlined,
  KingBed,
  MarkChatRead,
  MoreVert,
  OpenInNew,
  Person,
  SentimentVeryDissatisfied,
  SentimentVerySatisfied,
} from '@mui/icons-material';
import TabPanel from '../TabPanel/TabPanel';
import Comment from '../Comment/Comment';
import { constants } from '../../constants/constants';
import { Trans, useTranslation } from 'react-i18next';
import { IComment } from '../../shared/interfaces/IComment';
import Helper from '../../shared/helpers/Helper';
import { IBlock } from '../../shared/interfaces/IBlock';
import Block from '../Block/Block';
import StarRating from '../StarRating/StarRating';
import FeedbackComment from '../FeedbackComment/FeedbackComment';
import Status from '../Status/Status';
import OpinionIndicator from '../OpinionIndicator';
import Note from '../Notes/Note';
import DeleteReview from './DeleteReview';
import { AuthContext } from '../../providers/AuthProvider';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { IUserReplies } from '../../shared/interfaces/IUserReplies';
import ReviewService from '../../services/ReviewService';
import { customColors } from '../../constants/theme';
import PlatformService from '../../services/PlatformService';

interface ReviewProps {
  id: string | number;
  rating: number;
  platform: string;
  date: string | null;
  header?: string | null;
  text?: string | null;
  comment?: string | null;
  companyComment?: IComment | null;
  reviewLink?: string | null;
  username?: string | null;
  checked: boolean;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  position?: string | null;
  positionText?: string | null;
  jobTitle?: string | null;
  discipline?: string | null;
  subDiscipline?: string | null;
  location?: string | null;
  employmentStatus?: string | null;
  employmentDate?: string | null;
  feedbackOnStrengths?: string | null;
  feedbackOnThingsToImprove?: string | null;
  blocks?: IBlock[];
  title?: string | null;
  medicalPractitioner?: string | null;
  mandatoryAssessments?: IBlock[];
  optionalAssessments?: IBlock[];
  textCompanyPros?: string | null;
  textCompanyCons?: string | null;
  treatmentDuration?: string | null;
  gender?: string | null;
  age?: string | null;
  waitingTimeAppointment?: string | null;
  waitingTimeInWaitingRoom?: string | null;
  positiveFeedback?: string | null;
  negativeFeedback?: string | null;
  stayDuration?: string | null;
  stayDate?: string | null;
  travelerType?: string | null;
  roomType?: string | null;
  reviewType?: string | null;
  dateVisited?: string | null;
  traveledAs?: string[];
  travelDuration?: string | null;
  ratingValue: number;
  profilePicture: string | null;
  reviewerTitle: string | null;
  name: string | null;
  reviewTitle: string | null;
  reviewText: string | null;
  url?: string | null;
  languageCode: string | null;
  metaData: string | null;
  uniqueId: string | null;
  verifiedOrder: false;
  note: string;
  deletion: boolean;
  userReplies?: IUserReplies[];
  response: {
    id: number;
    date: string | null;
    name: string | null;
    comment: string | null;
  };
  company: string;
  status: string[];
  currentStatus: string;
  deleted: boolean;
}

const ReviewContainer = styled.div<{ $checked: boolean }>`
  padding: ${(props) => props.theme.spacing(1)};
  margin-bottom: ${(props) => props.theme.spacing(2)};
  border: 1px solid
    ${(props) => (props.$checked ? props.theme.palette.primary.main : props.theme.palette.divider)};
  border-radius: ${(props) => props.theme.shape.borderRadius}px;
  background-color: ${(props) =>
    props.$checked
      ? `rgba(${customColors.primary.main}, 0.1)`
      : props.theme.palette.background.paper};
`;

const ReviewContent = styled.div`
  padding-top: ${(props) => props.theme.spacing(2)};
  padding-bottom: ${(props) => props.theme.spacing(2)};
  padding-left: ${(props) => props.theme.spacing(7)};
  padding-right: ${(props) => props.theme.spacing(7)};
`;

const ReviewHeader = styled.div`
  display: flex;
  align-items: center;
  margin-top: ${(props) => props.theme.spacing(1)};
`;

const ReviewFooter = styled.div`
  display: flex;
  flex-direction: column;
`;

const PlatformLogo = styled.img`
  max-height: 32px;
  max-width: 76px;
  margin-left: ${(props) => props.theme.spacing(1)};
`;

const Review: FC<ReviewProps> = ({
  id,
  rating,
  platform,
  date,
  header,
  text,
  comment,
  companyComment,
  username,
  reviewLink,
  checked,
  onChange,
  position,
  positionText,
  jobTitle,
  discipline,
  subDiscipline,
  location,
  employmentStatus,
  employmentDate,
  feedbackOnStrengths,
  feedbackOnThingsToImprove,
  blocks,
  title,
  medicalPractitioner,
  mandatoryAssessments,
  optionalAssessments,
  textCompanyPros,
  textCompanyCons,
  treatmentDuration,
  gender,
  age,
  waitingTimeAppointment,
  waitingTimeInWaitingRoom,
  positiveFeedback,
  negativeFeedback,
  stayDate,
  stayDuration,
  roomType,
  travelerType,
  reviewType,
  dateVisited,
  traveledAs,
  travelDuration,
  name,
  ratingValue,
  profilePicture,
  reviewText,
  url,
  note,
  deletion,
  status,
  currentStatus,
  deleted,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { hasRole } = useContext(AuthContext);
  const [showFullText, setShowFullText] = useState(false);
  const [showAllBlocks, setShowAllBlocks] = useState(false);
  const [currentTab, setCurrentTab] = useState<number | boolean>(false);
  const [selectedStatus, setSelectedStatus] = useState<string>('');

  const handleTabChange = (event: SyntheticEvent, newValue: number | boolean) => {
    setCurrentTab(newValue);
  };

  const handleTabPanelClose = () => {
    setCurrentTab(false);
  };

  const handleFilterStatusChange = async (event: SelectChangeEvent) => {
    const currentStatus = event.target.value;
    setSelectedStatus(currentStatus);
    if (currentStatus === '') {
      await ReviewService.saveStatus(id, 'Status');
    } else {
      await ReviewService.saveStatus(id, currentStatus);
    }
  };

  return (
    <ReviewContainer $checked={checked}>
      <ReviewHeader>
        <Checkbox
          id={typeof id === 'number' ? `${id}` : id}
          checked={checked}
          onChange={onChange}
          sx={{ display: 'none', mr: theme.spacing(1) }}
        />
        <StarRating
          value={rating || ratingValue}
          sx={{ ml: theme.spacing(7) }}
        />
        <Box sx={{ display: 'flex', ml: theme.spacing(1), cursor: 'pointer' }}>
          <Tooltip
            title={`${t('info_rating')}: ${rating || ratingValue}`}
            placement="top"
            arrow
          >
            <InfoOutlined
              fontSize="small"
              sx={{ color: theme.palette.primary.main }}
            />
          </Tooltip>
        </Box>
        <PlatformLogo src={constants.platform_logos[platform]} />
        {reviewType && (
          <Box>
            <Typography
              variant="caption"
              sx={{ ml: theme.spacing(1) }}
            >
              <Trans t={t}>{reviewType}</Trans>
            </Typography>
          </Box>
        )}
        {medicalPractitioner && (
          <Tooltip
            title={`${t('reviewed_doctor')}:`}
            placement="top"
            arrow
          >
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.secondary,
                cursor: 'pointer',
                ml: theme.spacing(1),
              }}
            >
              {medicalPractitioner}
            </Typography>
          </Tooltip>
        )}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            ml: 'auto',
            pr: reviewLink || url ? 1 : theme.spacing(1),
          }}
        >
          {deleted && hasRole('can-use-deleted-reviews') && (
            <Box marginRight={theme.spacing(4)}>
              <Tooltip
                title={
                  PlatformService.deletedCanBeOutdated(platform)
                    ? t('review_deleted_or_outdated')
                    : t('review_deleted')
                }
                arrow
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: theme.palette.error.main,
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 700,
                    fontFamily: 'Roboto, sans-serif',
                  }}
                  aria-disabled
                >
                  {t('deleted')}
                </Typography>
              </Tooltip>
            </Box>
          )}
          <Tooltip
            title={Helper.getFormattedDate(date, false)}
            arrow
          >
            <Typography
              variant="body2"
              sx={{ cursor: 'pointer' }}
            >
              {Helper.getFormattedDate(date, true)}
            </Typography>
          </Tooltip>
          {reviewLink && (
            <Tooltip
              title={t('to_review')}
              arrow
            >
              <Link
                href={reviewLink}
                underline="none"
                target="_blank"
                rel="noreferrer"
                sx={{ display: 'flex', alignItems: 'center', ml: theme.spacing(3) }}
              >
                <OpenInNew />
              </Link>
            </Tooltip>
          )}
          {hasRole('can-use-note') && (
            <Note
              note={note}
              id={id}
              type="review"
            />
          )}
          {hasRole('can-use-deletion') && (
            <DeleteReview
              id={id}
              deletion={deletion}
            />
          )}
          {url && (
            <Tooltip
              title={t('to_review')}
              arrow
            >
              <Link
                href={url}
                underline="none"
                target="_blank"
                rel="noreferrer"
                sx={{ display: 'flex', alignItems: 'center', ml: theme.spacing(3) }}
              >
                <OpenInNew />
              </Link>
            </Tooltip>
          )}
        </Box>
      </ReviewHeader>
      {(position || positionText) && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            ml: theme.spacing(6),
            px: theme.spacing(1),
          }}
        >
          {position && (
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.secondary,
                cursor: 'pointer',
              }}
            >
              {position}
            </Typography>
          )}
          {positionText && (
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.secondary,
                cursor: 'pointer',
              }}
            >
              {positionText}
            </Typography>
          )}
        </Box>
      )}
      {(jobTitle ||
        discipline ||
        subDiscipline ||
        location ||
        employmentStatus ||
        employmentDate) && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            ml: theme.spacing(6),
            px: theme.spacing(1),
          }}
        >
          {(employmentStatus || employmentDate) && (
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.secondary,
                cursor: 'pointer',
              }}
            >
              <Trans t={t}>{employmentStatus}</Trans>{' '}
              {employmentDate && `(${Helper.getFormattedDate(employmentDate, true)})`}
            </Typography>
          )}
          {(jobTitle || discipline || subDiscipline) && (
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.secondary,
                cursor: 'pointer',
              }}
            >
              {jobTitle} {discipline} {subDiscipline}
            </Typography>
          )}
          {location && (
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.secondary,
                cursor: 'pointer',
              }}
            >
              {location}
            </Typography>
          )}
        </Box>
      )}
      {treatmentDuration && (
        <Box sx={{ display: 'flex' }}>
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              cursor: 'pointer',
              ml: theme.spacing(7),
            }}
          >
            {t('treatment_of')} {treatmentDuration}
          </Typography>
        </Box>
      )}
      {gender && (
        <Typography
          variant="caption"
          sx={{
            color: theme.palette.text.secondary,
            cursor: 'pointer',
            ml: theme.spacing(7),
          }}
        >
          {gender}
        </Typography>
      )}
      {age && age !== 'nicht angegeben' && (
        <Typography
          variant="caption"
          sx={{
            color: theme.palette.text.secondary,
            cursor: 'pointer',
            ml: theme.spacing(7),
          }}
        >
          {' '}
          ({age})
        </Typography>
      )}
      {dateVisited && (
        <Typography
          sx={{ mt: theme.spacing(1), ml: theme.spacing(7), color: theme.palette.text.secondary }}
        >
          {t('date_visited')}: {dateVisited}
        </Typography>
      )}
      {username && !travelerType && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            mt: theme.spacing(2),
            ml: theme.spacing(7),
            color: theme.palette.text.secondary,
          }}
        >
          <Person fontSize="small" />
          <Typography
            variant="body1"
            sx={{ ml: theme.spacing(1) }}
          >
            {username}
          </Typography>
        </Box>
      )}
      {username && travelerType && (
        <Box sx={{ display: 'flex', ml: theme.spacing(7), color: theme.palette.text.secondary }}>
          <Person fontSize="small" />
          <Typography
            variant="body1"
            sx={{ ml: theme.spacing(1) }}
          >
            {username} ({travelerType})
          </Typography>
        </Box>
      )}
      {name && !profilePicture && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            mt: theme.spacing(2),
            ml: theme.spacing(7),
            color: theme.palette.text.secondary,
          }}
        >
          <Person fontSize="small" />
          <Typography
            variant="body1"
            sx={{ ml: theme.spacing(1) }}
          >
            {name}
          </Typography>
        </Box>
      )}
      {name && profilePicture && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            mt: theme.spacing(2),
            ml: theme.spacing(7),
            color: theme.palette.text.secondary,
          }}
        >
          <Avatar
            alt={name}
            src={profilePicture}
            imgProps={{ referrerPolicy: 'no-referrer' }}
            sx={{ width: 32, height: 32, fontSize: '0.85rem' }}
          >
            {name.charAt(0)}
          </Avatar>
          <Typography
            variant="body1"
            sx={{ ml: theme.spacing(1) }}
          >
            {name}
          </Typography>
        </Box>
      )}
      {traveledAs && traveledAs.length !== 0 && (
        <Typography
          variant="caption"
          sx={{ ml: theme.spacing(7), color: theme.palette.text.secondary }}
        >
          {traveledAs}
        </Typography>
      )}
      {roomType && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            ml: theme.spacing(7),
            color: theme.palette.text.secondary,
          }}
        >
          <KingBed fontSize="small" />
          <Typography
            variant="caption"
            sx={{ ml: theme.spacing(1) }}
          >
            {roomType}
          </Typography>
        </Box>
      )}
      {stayDate && travelDuration && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            ml: theme.spacing(7),
            color: theme.palette.text.secondary,
          }}
        >
          <Typography variant="caption">{`${stayDate} - ${travelDuration}`}</Typography>
        </Box>
      )}
      {stayDuration && stayDate && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            ml: theme.spacing(7),
            color: theme.palette.text.secondary,
          }}
        >
          <DateRange fontSize="small" />
          <Typography
            variant="caption"
            sx={{ ml: theme.spacing(1) }}
          >
            {stayDuration}
          </Typography>
          <Typography
            variant="caption"
            sx={{ mx: theme.spacing(0.5) }}
          >
            &#8901;
          </Typography>
          <Typography variant="caption">{stayDate}</Typography>
        </Box>
      )}
      {waitingTimeAppointment && (
        <Box sx={{ display: 'flex', mt: theme.spacing(2), ml: theme.spacing(7) }}>
          <HourglassBottom fontSize="small" />
          <Typography
            variant="body1"
            sx={{ ml: theme.spacing(1) }}
          >
            {t('appointment')} {waitingTimeAppointment}
          </Typography>
        </Box>
      )}
      {waitingTimeInWaitingRoom && (
        <Box sx={{ display: 'flex', mt: theme.spacing(1), ml: theme.spacing(7) }}>
          <HourglassBottom fontSize="small" />
          <Typography
            variant="body1"
            sx={{ ml: theme.spacing(1) }}
          >
            {t('waiting_room')} {waitingTimeInWaitingRoom}
          </Typography>
        </Box>
      )}
      {title && (
        <Typography
          variant="h6"
          sx={{
            mt: theme.spacing(1),
            ml: theme.spacing(7),
          }}
        >
          {title}
        </Typography>
      )}
      {header && (
        <Typography
          variant="h6"
          sx={{
            mt: theme.spacing(2),
            ml: theme.spacing(7),
          }}
        >
          {header}
        </Typography>
      )}
      {text && (
        <Typography
          variant="body2"
          sx={{ mt: theme.spacing(1), ml: theme.spacing(7) }}
        >
          {text}
        </Typography>
      )}
      {positiveFeedback && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            mt: theme.spacing(1),
            ml: theme.spacing(1),
          }}
        >
          <SentimentVerySatisfied sx={{ color: theme.palette.success.dark }} />
          <Typography sx={{ ml: theme.spacing(3) }}>{positiveFeedback}</Typography>
        </Box>
      )}
      <Divider sx={{ mt: theme.spacing(2), ml: theme.spacing(7) }} />
      {negativeFeedback && (
        <Box
          sx={{ display: 'flex', alignItems: 'center', mt: theme.spacing(1), ml: theme.spacing(1) }}
        >
          <SentimentVeryDissatisfied sx={{ color: theme.palette.error.dark }} />
          <Typography sx={{ ml: theme.spacing(3) }}>{negativeFeedback}</Typography>
        </Box>
      )}
      {blocks !== undefined && blocks.length > 0 && platform === 'mobile' && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            mt: theme.spacing(3),
            ml: theme.spacing(7),
            mb: theme.spacing(3),
          }}
        >
          {blocks
            .filter((value) => typeof value.rating === 'string')
            .filter((value) => value.title !== null)
            .map(({ rating, title }, index) => {
              return (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'flex-end',
                  }}
                  key={`Review_${id}_Block_${index}`}
                >
                  {rating === 'YES' ? (
                    <>
                      <CheckIcon sx={{ color: theme.palette.success.dark }} />
                      <Status
                        key={`Review_${id}_Block_${index}`}
                        text={title}
                        iconBefore={<OpinionIndicator rating={rating} />}
                        sx={{ mx: theme.spacing(1) }}
                      />
                    </>
                  ) : (
                    <>
                      <CloseIcon sx={{ color: theme.palette.error.dark }} />
                      <Status
                        key={`Review_${id}_Block_${index}`}
                        text={title}
                        iconBefore={<OpinionIndicator rating={rating} />}
                        sx={{ mx: theme.spacing(1) }}
                      />
                    </>
                  )}
                </Box>
              );
            })}
        </Box>
      )}
      {textCompanyPros && (
        <>
          <Typography sx={{ mt: theme.spacing(2), ml: theme.spacing(7), fontWeight: 700 }}>
            {t('pros')}
          </Typography>
          <Typography sx={{ ml: theme.spacing(7) }}>{textCompanyPros}</Typography>
        </>
      )}
      {textCompanyCons && (
        <>
          <Typography sx={{ mt: theme.spacing(2), ml: theme.spacing(7), fontWeight: 700 }}>
            {t('contras')}
          </Typography>
          <Typography sx={{ ml: theme.spacing(7) }}>{textCompanyCons}</Typography>
        </>
      )}
      {reviewText && (
        <Typography
          variant="body2"
          sx={{ mt: theme.spacing(2), ml: theme.spacing(7) }}
        >
          {reviewText}
        </Typography>
      )}
      <ReviewContent>
        {(feedbackOnStrengths || feedbackOnThingsToImprove) && (
          <Box
            sx={{
              display: 'flex',
              columnGap: theme.spacing(2),
              my: theme.spacing(2),
            }}
          >
            {feedbackOnStrengths && (
              <FeedbackComment
                input={{ type: 'feedbackOnStrengths', comment: feedbackOnStrengths }}
              />
            )}
            {feedbackOnThingsToImprove && (
              <FeedbackComment
                input={{ type: 'feedbackOnThingsToImprove', comment: feedbackOnThingsToImprove }}
              />
            )}
          </Box>
        )}
        {blocks !== undefined && blocks.length > 0 && !showAllBlocks && (
          <Box sx={{ mt: theme.spacing(1) }}>
            <Divider orientation="horizontal" />
            <Block
              rating={blocks.filter((value) => typeof value.rating === 'number')[0].rating}
              text={blocks.filter((value) => typeof value.rating === 'number')[0].text}
              title={blocks.filter((value) => typeof value.rating === 'number')[0].title}
            />
            <Button
              variant="outlined"
              size="small"
              startIcon={<MoreVert />}
              onClick={() => setShowAllBlocks(!showAllBlocks)}
              sx={{
                my: theme.spacing(1),
              }}
            >
              {t('show_more')}
            </Button>
          </Box>
        )}
        {blocks !== undefined && blocks.length > 0 && showAllBlocks && (
          <Box sx={{ mt: theme.spacing(3) }}>
            {blocks.map(({ rating, text, title }, index) => {
              return (
                <Box
                  key={`Review_${id}_Block_${index}`}
                  sx={{ mt: theme.spacing(1) }}
                >
                  <Divider orientation="horizontal" />
                  <Block
                    rating={rating}
                    text={text}
                    title={title}
                  />
                </Box>
              );
            })}
            <Button
              variant="outlined"
              size="small"
              startIcon={<ExpandLess />}
              onClick={() => setShowAllBlocks(!showAllBlocks)}
              sx={{
                mb: theme.spacing(1),
              }}
            >
              {t('show_less')}
            </Button>
          </Box>
        )}
        {mandatoryAssessments !== undefined &&
          mandatoryAssessments.length > 0 &&
          !showAllBlocks && (
            <Box sx={{ mt: theme.spacing(1) }}>
              <Divider orientation="horizontal" />
              <Block
                rating={mandatoryAssessments[0].rating}
                text={mandatoryAssessments[0].text}
                title={mandatoryAssessments[0].title}
              />
              <Button
                variant="outlined"
                size="small"
                startIcon={<MoreVert />}
                onClick={() => setShowAllBlocks(!showAllBlocks)}
                sx={{
                  my: theme.spacing(1),
                }}
              >
                {t('show_more')}
              </Button>
            </Box>
          )}
        {mandatoryAssessments !== undefined && mandatoryAssessments.length > 0 && showAllBlocks && (
          <Box sx={{ mt: theme.spacing(3) }}>
            {mandatoryAssessments.map(({ rating, original_rating, title }, index) => {
              return (
                <Box
                  key={`Review_${id}_assessment_${index}`}
                  sx={{ mt: theme.spacing(1) }}
                >
                  <Divider orientation="horizontal" />
                  <Block
                    rating={rating}
                    original_rating={original_rating}
                    title={title}
                  />
                </Box>
              );
            })}
          </Box>
        )}
        {optionalAssessments !== undefined && optionalAssessments.length > 0 && showAllBlocks && (
          <Box sx={{ mt: theme.spacing(3) }}>
            <Typography
              variant="h6"
              sx={{ mb: theme.spacing(1) }}
            >
              Optionale Noten
            </Typography>
            {optionalAssessments.map(({ rating, original_rating, title }, index) => {
              return (
                <Box
                  key={`Review_${id}_OptAssessment_${index}`}
                  sx={{ mt: theme.spacing(1) }}
                >
                  <Divider orientation="horizontal" />
                  <Block
                    rating={rating}
                    original_rating={original_rating}
                    title={title}
                  />
                </Box>
              );
            })}
          </Box>
        )}
        {showAllBlocks && mandatoryAssessments !== undefined && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<ExpandLess />}
            onClick={() => setShowAllBlocks(!showAllBlocks)}
            sx={{
              mb: theme.spacing(1),
            }}
          >
            {t('show_less')}
          </Button>
        )}
      </ReviewContent>
      {companyComment && (
        <ReviewFooter>
          <Box sx={{ pl: theme.spacing(7), width: '100%' }}>
            <Box sx={{ borderBottom: currentTab === false ? 0 : 1, borderColor: 'divider' }}>
              <Tabs
                value={currentTab}
                onChange={handleTabChange}
                aria-label={t('comment_actions')}
              >
                <Tab
                  icon={<MarkChatRead />}
                  aria-label={t('comment')}
                />
              </Tabs>
            </Box>
            <TabPanel
              heading={platform === 'kununu' ? `${t('heading_kununu')}` : `${t('comment')}`}
              value={currentTab}
              index={0}
              onCloseClick={handleTabPanelClose}
            >
              <Comment companyComment={companyComment} />
            </TabPanel>
          </Box>
        </ReviewFooter>
      )}
      {comment && (
        <ReviewFooter>
          <Box sx={{ pl: theme.spacing(7), width: '100%' }}>
            <Box sx={{ borderBottom: currentTab === false ? 0 : 1, borderColor: 'divider' }}>
              <Tabs
                value={currentTab}
                onChange={handleTabChange}
                aria-label={t('comment_actions')}
              >
                <Tab
                  icon={<MarkChatRead />}
                  aria-label={t('comment')}
                />
              </Tabs>
            </Box>
            <TabPanel
              heading={platform === 'kununu' ? `${t('heading_kununu')}` : `${t('comment')}`}
              value={currentTab}
              index={0}
              onCloseClick={handleTabPanelClose}
            >
              <Comment
                companyComment={{
                  comment: comment,
                  date: date,
                  name: name,
                  position: '',
                  timestamp: '',
                  username: '',
                  userName: '',
                }}
              />
            </TabPanel>
          </Box>
        </ReviewFooter>
      )}
      {status &&
        (hasRole('can-use-status') ? (
          <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-end' }}>
            <FormControl
              size="small"
              sx={{ minWidth: '284px', mt: theme.spacing(2) }}
            >
              <InputLabel id="status-label-filter-label">{currentStatus || 'Status'}</InputLabel>
              <Select
                labelId="status-label-filter-label"
                value={selectedStatus}
                onChange={handleFilterStatusChange}
                label={'Status'}
              >
                <MenuItem value="Status">
                  <em>-</em>
                </MenuItem>
                {Array.from(status).map((statusName: string, index) => {
                  return (
                    <MenuItem
                      key={index}
                      value={statusName}
                    >
                      {statusName}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Box>
        ) : null)}
    </ReviewContainer>
  );
};

export default Review;
